/**
 * The on-site clock card (F7, D86).
 *
 * Starts on arrival, pauses for a break, stops when the job is finished. The
 * running entry is read off the job's own time entries (the same rows
 * `actions.runningClock` reads), so the card agrees with the Time card and
 * needs no second read. The seconds tick on a timer that only runs while a
 * clock is open and is cleared on unmount.
 */
import { useEffect, useRef, useState } from "react";

import * as actions from "../lib/actions";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { elapsedMs, formatElapsed, runningFor, todayTotals } from "../lib/onsite";
import { durationLabel } from "../lib/timebook";
import type { JobDetailOut } from "../lib/types";
import { Card } from "./ui";

/** "Now", re-read every second while `active`; stands still otherwise. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

/** Statuses where a clock makes no sense: the job is over or called off. */
export const CLOCK_CLOSED = ["cancelled", "completed", "invoiced"];

export function OnSiteClock({ job, onChanged }: { job: JobDetailOut; onChanged: () => void }) {
  const { session } = useAuth();
  const userId = session?.userId ?? "";
  const entries = job.time_entries ?? [];
  const running = userId ? runningFor(entries, userId) : null;
  const now = useNow(running !== null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: "info" | "crit"; text: string } | null>(null);
  const note = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    note.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [said]);

  async function run(step: () => Promise<{ queued: boolean; message?: string }>, fallback: string): Promise<void> {
    if (!userId) {
      setSaid({ tone: "crit", text: "Sign in again to use the clock." });
      return;
    }
    setBusy(true);
    setSaid(null);
    try {
      const { queued, message } = await step();
      if (queued && message) setSaid({ tone: "info", text: message });
      onChanged();
    } catch (err) {
      setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : fallback });
    } finally {
      setBusy(false);
    }
  }

  if (CLOCK_CLOSED.includes(job.status)) return null;

  const totals = todayTotals(entries, now);
  const onBreak = running?.kind === "break";

  return (
    <Card title="On site" subtitle={onBreak ? "On break" : running ? "Clock running" : "Not arrived"}>
      {running ? (
        <div className="clock">
          <div className={`clock-time mono${onBreak ? " clock-break" : ""}`} role="timer" aria-live="off">
            {onBreak ? "On break" : "On site"} {formatElapsed(elapsedMs(running.started_at, now))}
          </div>
          <button
            type="button"
            className={`btn clock-button${onBreak ? " btn-primary" : ""}`}
            disabled={busy}
            onClick={() =>
              void run(
                () => (onBreak ? actions.resumeWork(job.id, userId) : actions.takeBreak(job.id, userId)),
                onBreak ? "Couldn't end the break." : "Couldn't start the break.",
              )
            }
          >
            {busy ? "Saving…" : onBreak ? "Back to work" : "Break"}
          </button>
        </div>
      ) : (
        <div className="clock">
          <p className="tiny muted clock-hint">The clock starts when you arrive and stops when the job is finished.</p>
          <button
            type="button"
            className="btn btn-primary clock-button"
            disabled={busy}
            onClick={() => void run(() => actions.arrive(job.id, userId), "Couldn't record arrival.")}
          >
            {busy ? "Recording…" : "I've arrived"}
          </button>
        </div>
      )}

      {(totals.onSite > 0 || totals.breaks > 0 || running) && (
        <dl className="kv clock-totals">
          <dt>On site today</dt>
          <dd className="mono">{durationLabel(totals.onSite)}</dd>
          <dt>Breaks today</dt>
          <dd className="mono">{durationLabel(totals.breaks)}</dd>
        </dl>
      )}

      {said && (
        <p
          ref={note}
          className={`notice notice-${said.tone}`}
          role={said.tone === "crit" ? "alert" : "status"}
          style={{ marginTop: 10 }}
        >
          {said.text}
        </p>
      )}
    </Card>
  );
}
